import { POSITION_ATTRIBUTES } from '../features/staff/api/constants';

// Helper function to get all hierarchy org units
const getAllHierarchyOrgUnits = async (engine, userOrgUnits) => {
  const allOrgUnits = new Set();
  const topLevelOrgUnits = new Set();

  for (const orgUnit of userOrgUnits) {
    // Add the org unit itself
    allOrgUnits.add(orgUnit.id);

    try {
      // Get its full details including parent
      const { orgUnit: details } = await engine.query({
        orgUnit: {
          resource: `organisationUnits/${orgUnit.id}`,
          params: { fields: 'id,name,level,path,parent[id]' }
        }
      });

      if (details.path) {
        // Add all org units in the path
        const pathParts = details.path.split('/').filter(id => id);
        pathParts.forEach(id => allOrgUnits.add(id));

        // Get the top-level org unit (B2nLFRVH7yw)
        if (pathParts.length > 0) {
          topLevelOrgUnits.add(pathParts[0]); // First in path is top-level
        }
      }

      // If has parent, add parent ID
      if (details.parent?.id) {
        allOrgUnits.add(details.parent.id);
      }

    } catch (error) {
      console.warn('Could not get details for org unit:', orgUnit.id, error);
    }
  }

  // CRITICAL: Get ALL children of top-level org units (includes siblings)
  for (const topLevelId of topLevelOrgUnits) {
    try {
      const { organisationUnits } = await engine.query({
        organisationUnits: {
          resource: 'organisationUnits',
          params: {
            filter: `parent.id:eq:${topLevelId}`, // Get direct children
            fields: 'id',
            paging: false
          }
        }
      });

      organisationUnits?.organisationUnits?.forEach(ou => {
        allOrgUnits.add(ou.id);
      });

      // Also get ALL descendants (not just direct children)
      const { allDescendants } = await engine.query({
        allDescendants: {
          resource: 'organisationUnits',
          params: {
            filter: `path:like:${topLevelId}`,
            fields: 'id',
            paging: false
          }
        }
      });

      allDescendants?.organisationUnits?.forEach(ou => {
        allOrgUnits.add(ou.id);
      });

    } catch (error) {
      console.warn('Could not get children for top-level:', topLevelId, error);
    }
  }

  console.log('🌳 Final hierarchy org units:', Array.from(allOrgUnits));
  return Array.from(allOrgUnits);
};

export const ensurePositionAccessibility = async (engine, userId, userOrgUnits) => {
  console.log('🔧 [POSITION ACCESS] Ensuring position accessibility for user:', userId);

  const orgUnitParam = userOrgUnits.map(ou => ou.id).join(';');

  try {
    // 1. First try to find position globally (without ouMode restriction)
    console.log('🔍 Trying global position search first...');
    const globalPosition = await findPositionGloballyUnrestricted(engine, userId);

    if (globalPosition) {
      console.log('✅ Found position globally:', globalPosition.trackedEntityInstance);

      // Check if position org unit is accessible
      const userOrgUnitIds = userOrgUnits.map(ou => ou.id);
      const positionAccessible = userOrgUnitIds.includes(globalPosition.orgUnit);

      if (positionAccessible) {
        console.log('✅ Position org unit is already accessible');
        return globalPosition;
      }

      // Position found but not accessible - grant access to the org unit
      console.log('🔑 Granting access to position org unit:', globalPosition.orgUnit);
      try {
        const accessGranted = await grantOrgUnitAccess(engine, userId, globalPosition.orgUnit);

        if (accessGranted) {
          console.log('✅ Position org unit access granted');
          return globalPosition;
        } else {
          console.warn('⚠️ Failed to grant position org unit access');
        }
      } catch (grantError) {
        console.error('❌ Error granting position org unit access:', grantError);
      }

      // Return position even if access grant failed (may still work)
      return globalPosition;
    }

    // 2. Fallback: Find user's position in accessible org units
    const { positions } = await engine.query({
      positions: {
        resource: 'trackedEntityInstances',
        params: {
          trackedEntityType: 'UGGo0Ebjl0E',
          ou: orgUnitParam,
          filter: `V66IFsw5kq6:eq:${userId}`,
          fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
          pageSize: 1
        }
      }
    });

    const userPosition = positions?.trackedEntityInstances?.[0];

    if (!userPosition) {
      console.log('⚠️ User has no position TEI in accessible org units');

      // Try hierarchy search
      console.log('🔄 Trying hierarchy search method...');
      return await findPositionGlobally(engine, userId, userOrgUnits);
    }

    const positionTei = userPosition.trackedEntityInstance;
    const positionOrgUnit = userPosition.orgUnit;

    console.log('🔍 Found position:', {
      tei: positionTei,
      currentOrgUnit: positionOrgUnit,
      userOrgUnits: userOrgUnits.map(ou => ou.id)
    });

    // 2. Check if position org unit is in user's accessible org units
    const userOrgUnitIds = userOrgUnits.map(ou => ou.id);
    const positionAccessible = userOrgUnitIds.includes(positionOrgUnit);

    if (positionAccessible) {
      console.log('✅ Position is already in accessible org unit');
      return userPosition;
    }

    // 3. Position is NOT accessible - need to move it or update sharing
    console.log('⚠️ Position is in inaccessible org unit. Attempting to move...');

    // Get top-level org unit from user's org units
    const topLevelOrgUnit = userOrgUnits.find(ou => ou.level <= 5) || userOrgUnits[0];

    if (!topLevelOrgUnit) {
      console.error('❌ No suitable org unit found for moving position');
      return userPosition; // Return original, but it won't be accessible
    }

    // 4. Move position to accessible org unit
    console.log(`🔄 Moving position from ${positionOrgUnit} to ${topLevelOrgUnit.id} (${topLevelOrgUnit.name})`);

    try {
      const updateResponse = await engine.mutate({
        resource: `trackedEntityInstances/${positionTei}`,
        type: 'update',
        data: {
          trackedEntityInstance: positionTei,
          trackedEntityType: 'UGGo0Ebjl0E',
          orgUnit: topLevelOrgUnit.id, // Move to accessible org unit
          attributes: userPosition.attributes // Keep all attributes
        }
      });

      console.log('✅ Position moved successfully:', updateResponse);

      // Return updated position
      return {
        ...userPosition,
        orgUnit: topLevelOrgUnit.id
      };

    } catch (updateError) {
      console.error('❌ Failed to move position:', updateError);

      // Fallback: Try to update sharing instead
      console.log('🔄 Attempting to update sharing permissions...');
      try {
        await engine.mutate({
          resource: 'sharing',
          type: 'update',
          data: {
            type: 'trackedEntityInstance',
            id: positionTei,
            object: {
              publicAccess: 'rwrw----',
              externalAccess: false,
              users: {
                [userId]: {
                  access: 'rwrw----',
                  id: userId
                }
              }
            }
          }
        });

        console.log('✅ Sharing permissions updated');
      } catch (sharingError) {
        console.error('❌ Failed to update sharing:', sharingError);
      }

      return userPosition; // Return original (may still be inaccessible)
    }

  } catch (error) {
    console.error('❌ Position accessibility check failed:', error);
    return null;
  }
};

const findPositionGloballyUnrestricted = async (engine, userId) => {
  try {
    // Search globally without ouMode restriction
    const response = await engine.query({
      positions: {
        resource: 'trackedEntityInstances',
        params: {
          trackedEntityType: 'UGGo0Ebjl0E',
          filter: `${POSITION_ATTRIBUTES.CURRENT_INCUMBENT}:eq:${userId}`,
          fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
          ouMode: 'ALL', // Search all org units
          pageSize: 1
        }
      }
    });

    return response.positions?.trackedEntityInstances?.[0];
  } catch (error) {
    console.error('Global unrestricted position search failed:', error);
    return null;
  }
};

const findPositionGlobally = async (engine, userId, userOrgUnits) => {
  // Since we can't query without ou parameter, we need a different approach

  // OPTION 1: Use hierarchy org units (like we already do)
  const hierarchyOrgUnits = await getAllHierarchyOrgUnits(engine, userOrgUnits);
  const hierarchyParam = hierarchyOrgUnits.join(';');

  try {
    const response = await engine.query({
      positions: {
        resource: 'trackedEntityInstances',
        params: {
          trackedEntityType: 'UGGo0Ebjl0E',
          ou: hierarchyParam,
          ouMode: 'ACCESSIBLE',
          filter: `${POSITION_ATTRIBUTES.CURRENT_INCUMBENT}:eq:${userId}`,
          fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
          pageSize: 1
        }
      }
    });

    return response.positions?.trackedEntityInstances?.[0];
  } catch (error) {
    console.error('Global position search failed:', error);
    return null;
  }
};

const grantOrgUnitAccess = async (engine, userId, orgUnitId) => {
  if (!userId || !orgUnitId) {
    console.log('❌ Missing userId or orgUnitId for access grant');
    return false;
  }

  try {
    console.log(`🔄 Granting org unit access:`, { userId, orgUnitId });

    // 1. Fetch full user with ALL details needed
    const { user: fullUser } = await engine.query({
      user: {
        resource: `users/${userId}`,
        params: {
          fields: 'id,firstName,surname,organisationUnits[id],dataViewOrganisationUnits[id],userCredentials[username]'
        }
      }
    });

    if (!fullUser) {
      console.error('❌ User not found');
      return false;
    }

    // 2. Check current access
    const currentOrgUnits = fullUser.organisationUnits?.map(ou => ou.id) || [];
    const currentDataViewUnits = fullUser.dataViewOrganisationUnits?.map(ou => ou.id) || [];

    const hasOrgUnitAccess = currentOrgUnits.includes(orgUnitId);
    const hasDataViewAccess = currentDataViewUnits.includes(orgUnitId);

    console.log('🔍 Current access status:', {
      hasOrgUnitAccess,
      hasDataViewAccess,
      currentOrgUnits,
      currentDataViewUnits
    });

    // 3. If already has access in BOTH arrays, return
    if (hasOrgUnitAccess && hasDataViewAccess) {
      console.log(`✅ User already has full access to org unit ${orgUnitId}`);
      return true;
    }

    // 4. Prepare updates - add to BOTH arrays if missing in either
    const updatedOrgUnits = hasOrgUnitAccess
      ? fullUser.organisationUnits
      : [...fullUser.organisationUnits, { id: orgUnitId }];

    const updatedDataViewOrgUnits = hasDataViewAccess
      ? fullUser.dataViewOrganisationUnits
      : [...fullUser.dataViewOrganisationUnits, { id: orgUnitId }];

    console.log('📝 Update payload:', {
      organisationUnits: updatedOrgUnits.map(ou => ou.id),
      dataViewOrganisationUnits: updatedDataViewOrgUnits.map(ou => ou.id)
    });

    // 5. Update user
    const updateData = {
      organisationUnits: updatedOrgUnits.map(ou => ({ id: ou.id })),
      dataViewOrganisationUnits: updatedDataViewOrgUnits.map(ou => ({ id: ou.id }))
    };

    console.log('📤 Sending update data:', updateData);

    const result = await engine.mutate({
      resource: `users/${userId}`,
      type: 'update',
      data: updateData
    });

    console.log(`✅ Successfully granted access to org unit ${orgUnitId}`);
    console.log('Update result:', result);

    return true;

  } catch (error) {
    console.error('❌ Failed to grant org unit access:', error);

    // Provide more detailed error information
    if (error.details) {
      console.error('Error details:', error.details);
    }
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }

    return false;
  }
};